<script>
    import { cloneDeep, get } from 'lodash';
    import Axios, { validateStatus } from 'util/service/index';
    import { FORM_SUBMISSION_STATE } from 'util/vue/form';

    /**
     * Return a general error message in case there are no given errors
     * @param {Object} data - The payload
     * @param {number} status - The status code
     * @param {string} statusText - A text describing the status code
     * @return {string[]} A list of general errors
     */
    function createGeneralErrors({ data, status, statusText }) {
        const errors = get(data, 'messages.error', []);

        if (!errors.length) {
            return [`${ status } - ${ statusText }`];
        }

        return errors;
    }

    /**
     * Create a new form submission request
     * @param {HTMLElement} form - The form element
     * @param {string} url - The form submission endpoint
     * @return {Promise} The request
     */
    function createSubmitRequest(form, url) {
        const formData = new FormData(form);
        return Axios.post(url, formData, { validateStatus });
    }

    export default {
        inheritAttrs: false,
        props: {
            success_url: {
                type: String,
                required: true,
            },
            url: {
                type: String,
                required: true,
            },
            /** @type {IFormFieldset[]} */
            fieldsets: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                submissionState: FORM_SUBMISSION_STATE.PRISTINE,
                successUrl: null,
                protectedFieldsets: [],
                fieldErrors: [],
                generalErrors: [],
            };
        },
        methods: {
            bindFormProps() {
                const { url, bindFieldsetProps, generalErrors, protectedFieldsets, submissionState, successUrl } = this;
                return {
                    action: url,
                    bindFieldsetProps,
                    fieldsets: protectedFieldsets,
                    generalErrors,
                    submissionState,
                    successUrl,
                };
            },
            bindFieldsetProps(fieldset) {
                const { bindFieldProps } = this;
                return { bindFieldProps, ...fieldset };
            },
            bindFieldProps(field) {
                const fieldData = this.addFieldErrors(field);

                const { onFormSubmit, submissionState } = this;
                return { onFormSubmit, submissionState, ...fieldData };
            },
            onFormSubmit() {
                if (this.submissionState === FORM_SUBMISSION_STATE.SUBMITTING) {
                    return Promise.reject();
                }

                this.submissionState = FORM_SUBMISSION_STATE.SUBMITTING;

                const SubmitRequest = createSubmitRequest(this.$el, this.url);
                SubmitRequest.then(this.onSubmitSuccess, this.onSubmitError);

                return SubmitRequest;
            },
            onSubmitSuccess(data) {
                this.submissionState = FORM_SUBMISSION_STATE.SUCCESS;
                this.successUrl = data.success_url || this.success_url;

                this.resetForm();
            },
            onSubmitError({ response }) {
                this.submissionState = FORM_SUBMISSION_STATE.ERROR;
                this.fieldErrors = get(response, 'data.fields', []);
                this.generalErrors = createGeneralErrors(response);
            },
            resetForm() {
                this.protectedFieldsets = cloneDeep(this.fieldsets);
                this.fieldErrors = [];
                this.generalErrors = [];
            },
            addFieldErrors(field) {
                if (!field.id) {
                    return field;
                }

                const errorData = this.fieldErrors.find(error => error.id === field.id);

                this.$set(field.messages, 'error', get(errorData, 'messages.error', []));
                this.$set(field.state, 'error', get(errorData, 'state.error', false));

                return field;
            },
        },
        watch: {
            fieldsets: {
                handler(value) {
                    this.protectedFieldsets = cloneDeep(value);
                },
                immediate: true,
            },
        },
        render() {
            return this.$scopedSlots.default(this.bindFormProps());
        },
    };
</script>
